import { cva, cx } from "cva";
import Image from "next/image";
import { useAnalytics } from "~/components/Analytics";
import { BuildHTML } from "~/components/BuildHtml/BuildHtml";
import { CTALink } from "~/landing-pages/components/CTA/CTALink/CTALink";
import {
  primaryButtonStyle,
  secondaryButtonStyle,
} from "~/landing-pages/components/CTA/cta-shared-styles";
import { heroHeadlineFontStyle } from "~/landing-pages/shared-landing-page-styles";
import { tagToClassNameMap } from "~/landing-pages/utils/tagToClassNameMap";
import { Body } from "~/ui-library";

export interface HeroWithAppScreenshotProps {
  blockIndex: number;
  appearance: "light" | "dark";
  imageUrl: string;
  imageAlt: string;
  headline: string;
  description: string;
  primaryCtaLink: string;
  primaryCtaText: string;
  secondaryCtaLink?: string;
  secondaryCtaText?: string;
}

const containerStyles = cva(["relative isolate pt-14"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      dark: "bg-gray-900",
      light: "bg-white",
    },
  },
});

const headlineStyles = cva("", {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      dark: "text-white",
      light: "text-gray-900",
    },
  },
});

const descriptionStyles = cva(["mt-6 text-[1.125rem]"], {
  defaultVariants: {
    appearance: "light",
  },
  variants: {
    appearance: {
      dark: "text-gray-300",
      light: "text-gray-700",
    },
  },
});

export const HeroWithAppScreenshot = ({
  blockIndex,
  appearance,
  imageUrl,
  imageAlt,
  headline,
  description,
  primaryCtaLink,
  primaryCtaText,
  secondaryCtaLink,
  secondaryCtaText,
}: HeroWithAppScreenshotProps): JSX.Element => {
  const hasSecondaryCTA = !!(secondaryCtaLink && secondaryCtaText);

  const { analyticsEnhancedTrack } = useAnalytics();
  const analyticsVariables = {
    blockIndex,
    appearance,
  };

  const primaryButtonClick = () =>
    analyticsEnhancedTrack("Clicked Primary CTA", {
      ...analyticsVariables,
      linkText: primaryCtaText,
      linkUrl: primaryCtaLink,
    });
  const secondaryButtonClick = () =>
    analyticsEnhancedTrack("Clicked Secondary CTA", {
      ...analyticsVariables,
      linkText: secondaryCtaText,
      linkUrl: secondaryCtaLink,
    });

  return (
    <div className={containerStyles({ appearance })}>
      {appearance === "dark" && (
        <div
          className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80"
          aria-hidden="true"
        >
          <div
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-brand-indigo via-brand to-brand-teal-900 opacity-20 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>
      )}
      <div className="py-8 sm:py-12 lg:pb-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1
              className={cx(
                heroHeadlineFontStyle(),
                headlineStyles({ appearance })
              )}
            >
              {headline}
            </h1>
            <Body className={descriptionStyles({ appearance })}>
              <BuildHTML
                html={description}
                tagToClassNameMap={tagToClassNameMap}
              />
            </Body>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <CTALink
                href={primaryCtaLink}
                onClick={primaryButtonClick}
                className={primaryButtonStyle({ appearance })}
              >
                {primaryCtaText}
              </CTALink>
              {hasSecondaryCTA && (
                <CTALink
                  href={secondaryCtaLink}
                  onClick={secondaryButtonClick}
                  className={secondaryButtonStyle({ appearance })}
                >
                  {secondaryCtaText}
                </CTALink>
              )}
            </div>
          </div>
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={2432}
            height={1442}
            className="mt-16 rounded-lg bg-white/5 shadow-3 ring-1 ring-white/10 sm:mt-24"
          />
        </div>
      </div>
      {appearance === "dark" && (
        <div
          className="absolute inset-x-0 top-[calc(100%-13rem)] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[calc(100%-30rem)]"
          aria-hidden="true"
        >
          <div
            className="relative left-[calc(50%+3rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 bg-gradient-to-tr from-brand-indigo via-brand to-brand-teal-900 opacity-20 sm:left-[calc(50%+36rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
            }}
          />
        </div>
      )}
    </div>
  );
};
